"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export function EventShowcaseServices({ section }) {
  return (
    <section className="py-10 relative overflow-hidden bg-transparent w-full font-sans">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          
          {/* Left Column: Premium Framed Image with Glow */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative lg:col-span-7 w-full"
          >
            {/* Soft decorative glow behind the image */}
            <div className="absolute inset-0 bg-[#f1833b]/10 rounded-[32px] blur-3xl -z-10 transform scale-95" />
            
            <div className="p-1.5 bg-white/5 border border-white/10 rounded-[24px] shadow-2xl backdrop-blur-md hover:scale-[1.01] transition-transform duration-300">
              <Image
                src={section.image}
                alt="Event Management Website Builder"
                width={800}
                height={500}
                className="w-full h-auto rounded-[18px] object-cover border border-white/10 shadow-inner"
              />
            </div>
          </motion.div>

          {/* Right Column: Copywriting & Value Props */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-col gap-4 lg:col-span-5"
          >
            <h2 className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight`}>
              Showcase your{" "}
              <span className="relative inline-block text-[#f1833b]">
                services
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#f1833b]" preserveAspectRatio="none" viewBox="0 0 100 20">
                  <path d="M0,10 Q25,0 50,10 T100,10" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
                </svg>
              </span>
            </h2>
            
            <p className="text-gray-300 text-lg leading-relaxed mt-2">
              {section.desc}
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {section.features?.map((feat, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#f1833b]/20 flex items-center justify-center text-[#f1833b] font-bold text-sm">
                    ✓
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-base">{feat.title}</h4>
                    <p className="text-gray-400 text-sm mt-0.5">{feat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

export function EventQuotations({ section }) {
  return (
    <section className="py-10 bg-transparent w-full font-sans relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Copywriting */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col gap-4 lg:col-span-5 order-2 lg:order-1"
          >
            <h2 className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight`}>
              Professional{" "}
              <span className="relative inline-block text-[#f1833b]">
                Quotations
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#f1833b]" preserveAspectRatio="none" viewBox="0 0 100 20">
                  <path d="M0,10 Q25,0 50,10 T100,10" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
                </svg>
              </span>
            </h2>
            
            <p className="text-gray-300 text-lg leading-relaxed mt-2">
              {section.desc}
            </p>

            <div className="flex flex-col gap-3.5 mt-4">
              {section.features?.map((feat, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="text-[#f1833b] font-bold text-xl">✦</span>
                  <span className="text-gray-200 text-base">{feat}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Column: Premium Multi-Card Collage */}
          <div className="lg:col-span-7 w-full order-1 lg:order-2">
            {/* Background Glow */}
            <div className="absolute inset-0 bg-[#fbbf24]/5 rounded-full blur-3xl -z-10 transform scale-50" />
            
            <div className="grid grid-cols-2 gap-2 md:gap-4 items-center w-full">
              {/* Left Pair */}
              <div className="flex items-center justify-center md:justify-end py-2 md:py-0">
                <motion.div
                  initial={{ opacity: 0, y: 20, rotate: -2 }}
                  whileInView={{ opacity: 1, y: 0, rotate: -3 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="w-[72%] p-1 bg-white border border-gray-200 rounded-2xl shadow-2xl z-10 translate-y-4 -mr-[25%] hover:scale-[1.05] hover:z-30 transition-all duration-300"
                >
                  <Image
                    src={section.images[0]}
                    alt="Quotation Details"
                    width={300}
                    height={200}
                    className="w-full h-auto rounded-xl object-cover"
                  />
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: -20, rotate: 2 }}
                  whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="w-[72%] p-1 bg-white border border-gray-200 rounded-2xl shadow-2xl z-0 -translate-y-4 hover:scale-[1.05] hover:z-30 transition-all duration-300"
                >
                  <Image
                    src={section.images[1]}
                    alt="Quotation Editor"
                    width={300}
                    height={200}
                    className="w-full h-auto rounded-xl object-cover"
                  />
                </motion.div>
              </div>

              {/* Right Pair */}
              <div className="flex items-center justify-center md:justify-start py-2 md:py-0">
                <motion.div
                  initial={{ opacity: 0, y: -20, rotate: -2 }}
                  whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="w-[72%] p-1 bg-white border border-gray-200 rounded-2xl shadow-2xl z-0 -translate-y-4 hover:scale-[1.05] hover:z-30 transition-all duration-300"
                >
                  <Image
                    src={section.images[2]}
                    alt="Mobile Quotation Preview"
                    width={300}
                    height={200}
                    className="w-full h-auto rounded-xl object-cover"
                  />
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 20, rotate: 2 }}
                  whileInView={{ opacity: 1, y: 0, rotate: 3 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="w-[72%] p-1 bg-white border border-gray-200 rounded-2xl shadow-2xl z-20 translate-y-4 -ml-[25%] hover:scale-[1.05] hover:z-30 transition-all duration-300"
                >
                  <Image
                    src={section.images[3]}
                    alt="Quotation Total Invoice"
                    width={300}
                    height={200}
                    className="w-full h-auto rounded-xl object-cover"
                  />
                </motion.div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

export function EventCoordination({ section }) {
  return (
    <section className="py-10 bg-transparent w-full font-sans relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] text-center">
        
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto mb-10"
        >
          <h2 className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4`}>
            Seamless <span className="text-[#f1833b]">Coordination</span>
          </h2>
          <p className="text-gray-300 text-base md:text-lg">
            {section.desc}
          </p>
        </motion.div>

        {/* Step Cards with Connecting Arrows */}
        <div className="flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-8 w-full mt-6 relative z-10">
          
          {/* Background Glow */}
          <div className="absolute inset-0 bg-[#fbbf24]/5 rounded-full blur-3xl -z-10 transform scale-50" />
          
          {/* Step 1: Pipeline */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, x: -30 }}
            whileInView={{ opacity: 1, scale: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="w-full lg:w-[18%] p-1 bg-white/5 border border-white/10 rounded-2xl shadow-xl hover:scale-[1.02] transition-transform duration-300 flex-shrink-0"
          >
            <Image
              src={section.images[0]}
              alt="Event Pipeline"
              width={250}
              height={180}
              className="w-full h-auto rounded-xl object-cover border border-white/5 shadow-md"
            />
          </motion.div>

          {/* Arrow 1 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6, rotate: 180 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 270 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 100, delay: 0.2 }}
            className="flex-shrink-0 hidden lg:block"
          >
            <Image
              src={section.arrowIcon}
              alt=""
              width={32}
              height={32}
              className="w-8 h-8 select-none pointer-events-none opacity-40 invert filter sepia saturate-150 hue-rotate-15"
            />
          </motion.div>

          {/* Step 2: Events Management Panel (Center Large) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="w-full lg:w-[58%] p-1.5 bg-white/5 border border-white/15 rounded-3xl shadow-2xl hover:scale-[1.01] transition-transform duration-300 z-10"
          >
            <Image
              src={section.images[1]}
              alt="Odoo Events Dashboard"
              width={800}
              height={500}
              className="w-full h-auto rounded-2xl object-cover border border-white/5 shadow-lg"
            />
          </motion.div>

          {/* Arrow 2 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6, rotate: 180 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 270 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 100, delay: 0.3 }}
            className="flex-shrink-0 hidden lg:block"
          >
            <Image
              src={section.arrowIcon}
              alt=""
              width={32}
              height={32}
              className="w-8 h-8 select-none pointer-events-none opacity-40 invert filter sepia saturate-150 hue-rotate-15"
            />
          </motion.div>

          {/* Step 3: Mobile Ticket Sales */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, x: 30 }}
            whileInView={{ opacity: 1, scale: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-full lg:w-[18%] p-1 bg-white/5 border border-white/10 rounded-2xl shadow-xl hover:scale-[1.02] transition-transform duration-300 flex-shrink-0"
          >
            <Image
              src={section.images[2]}
              alt="Mobile Event Experience"
              width={250}
              height={180}
              className="w-full h-auto rounded-xl object-cover border border-white/5 shadow-md"
            />
          </motion.div>

        </div>

      </div>
    </section>
  );
}

export function EventLogistics({ section }) {
  return (
    <section className="py-10 bg-transparent w-full font-sans relative overflow-hidden">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] text-center">
        
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto mb-12"
        >
          <h2 className={`${caveat.className} text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4`}>
            Event <span className="text-[#f1833b]">Logistics</span>
          </h2>
          <p className="text-gray-300 text-base md:text-lg">
            {section.desc}
          </p>
        </motion.div>

        {/* 3-Column Display */}
        <div className="grid md:grid-cols-3 gap-8 md:gap-10 w-full mt-6 relative z-10">
          {section.items?.map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.15 }}
              className="flex flex-col gap-2 group cursor-pointer"
            >
              <h4 className={`${caveat.className} text-3xl md:text-4xl font-bold text-white mb-2 text-left group-hover:text-[#f1833b] transition-colors leading-tight`}>
                {item.label}
              </h4>
              
              <div className="w-full bg-white border border-gray-200 rounded-2xl p-1.5 shadow-xl hover:scale-[1.02] hover:shadow-2xl transition-all duration-300">
                <Image
                  src={item.image}
                  alt={item.label}
                  width={300}
                  height={220}
                  className="w-full h-auto rounded-xl object-cover"
                />
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
